/**
 * <numeric-range>
 * Presents a discrete-value "list" facet (e.g. the rope diameter / tjocklek metafield)
 * as a dual-handle range slider that SNAPS to the available values by index.
 *
 * The real <input type="checkbox"> options are kept in the DOM (hidden when JS is
 * active) and carry the actual filter state, so the existing FacetFilters form
 * submission and the no-JS fallback keep working unchanged. Moving the handles
 * checks every option whose numeric value falls within [min, max] and dispatches a
 * single bubbling 'input' event, which FacetFilters already listens for.
 *
 * Index-based positioning is deliberate: the values are unevenly spaced
 * (8, 10, 11, 11.35, 11.5 … 12.7), so each value gets equal slider travel.
 */
if (!customElements.get('numeric-range')) {
  class NumericRange extends HTMLElement {
    constructor() {
      super();
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        this.init();
      }
    }

    init() {
      this.unit = this.dataset.unit || '';
      this.minSlider = this.querySelector('.js-numeric-range-min');
      this.maxSlider = this.querySelector('.js-numeric-range-max');
      this.minLabel = this.querySelector('.js-numeric-range-min-label');
      this.maxLabel = this.querySelector('.js-numeric-range-max-label');

      // Build the sorted option list from the discrete checkboxes.
      this.options = Array.from(this.querySelectorAll('input[type="checkbox"]'))
        .map((cb) => ({ cb, num: parseFloat(String(cb.value).replace(',', '.')) }))
        .filter((option) => !Number.isNaN(option.num))
        .sort((a, b) => a.num - b.num);

      if (this.options.length < 2 || !this.minSlider || !this.maxSlider) {
        // Nothing meaningful to slide over – leave the checkbox fallback visible.
        this.classList.add('numeric-range--inactive');
        return;
      }

      const lastIndex = this.options.length - 1;
      [this.minSlider, this.maxSlider].forEach((slider) => {
        slider.min = 0;
        slider.max = lastIndex;
        slider.step = 1;
      });

      // Start the handles at the currently-active range, or the full range.
      const checkedIndexes = this.options.reduce(
        (acc, option, index) => (option.cb.checked ? acc.concat(index) : acc),
        []
      );
      this.minSlider.value = checkedIndexes.length ? Math.min(...checkedIndexes) : 0;
      this.maxSlider.value = checkedIndexes.length ? Math.max(...checkedIndexes) : lastIndex;

      this.minSlider.addEventListener('input', this.handleInput.bind(this));
      this.maxSlider.addEventListener('input', this.handleInput.bind(this));
      this.minSlider.addEventListener('change', this.handleChange.bind(this));
      this.maxSlider.addEventListener('change', this.handleChange.bind(this));

      this.updateLabels();
    }

    get lowIndex() {
      return Math.min(Number(this.minSlider.value), Number(this.maxSlider.value));
    }

    get highIndex() {
      return Math.max(Number(this.minSlider.value), Number(this.maxSlider.value));
    }

    format(num) {
      return this.unit ? `${num} ${this.unit}` : `${num}`;
    }

    /**
     * While dragging: keep the handles from crossing and update the readout, but
     * stop the event bubbling so the grid isn't refetched on every tick.
     * @param {Event} evt
     */
    handleInput(evt) {
      if (Number(this.minSlider.value) > Number(this.maxSlider.value)) {
        if (evt.target === this.minSlider) {
          this.maxSlider.value = this.minSlider.value;
        } else {
          this.minSlider.value = this.maxSlider.value;
        }
      }
      this.updateLabels();
      evt.stopPropagation();
    }

    /**
     * On release: tick every checkbox inside the selected range, then fire one
     * bubbling 'input' event for FacetFilters to pick up and re-query.
     */
    handleChange() {
      const lowValue = this.options[this.lowIndex].num;
      const highValue = this.options[this.highIndex].num;

      this.options.forEach((option) => {
        option.cb.checked = option.num >= lowValue && option.num <= highValue;
      });

      this.updateLabels();
      this.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateLabels() {
      if (this.minLabel) this.minLabel.textContent = this.format(this.options[this.lowIndex].num);
      if (this.maxLabel) this.maxLabel.textContent = this.format(this.options[this.highIndex].num);
    }
  }

  customElements.define('numeric-range', NumericRange);
}
